import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpContext, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import { ApiEnvelope } from '../models/auth-api.model';
import { PageResult } from '../models/api.model';
import {
  CourseStartReadiness,
  StudentCourseCatalogItem,
  StudentCurrentLesson,
  StudentCurriculumCourse,
  StudentCurriculumEnrollment,
  CurriculumLessonProgress,
} from '../models/curriculum-api.model';
import { SKIP_ERROR_TOAST } from '../interceptors/error-toast.interceptor';

/** Curriculum endpoints: courses, enrollments, and the catalogue. */
@Injectable({ providedIn: 'root' })
export class CurriculumApiService {
  private readonly http = inject(HttpClient);
  private readonly base = `${environment.apiUrl}/curriculum/student`;
  private readonly catalogUrl = `${environment.apiUrl}/student/allcourses`;

  courses(pageNumber = 1, pageSize = 50): Observable<PageResult<StudentCurriculumCourse>> {
    return this.http
      .get<ApiEnvelope<PageResult<StudentCurriculumCourse>>>(`${this.base}/courses`, {
        params: new HttpParams().set('pageNumber', pageNumber).set('pageSize', pageSize),
      })
      .pipe(map((r) => r.data));
  }

  course(id: string): Observable<StudentCurriculumCourse> {
    return this.http
      .get<ApiEnvelope<StudentCurriculumCourse>>(`${this.base}/courses/${id}`)
      .pipe(map((r) => r.data));
  }

  /**
   * 403 when not enrolled is expected on the course detail screen —
   * skip the global error toast for that probe.
   */
  currentLesson(courseId: string): Observable<StudentCurrentLesson> {
    return this.http
      .get<ApiEnvelope<StudentCurrentLesson>>(`${this.base}/courses/${courseId}/current-lesson`, {
        context: new HttpContext().set(SKIP_ERROR_TOAST, true),
      })
      .pipe(map((r) => r.data));
  }

  /** Whether Get started can proceed (at least one lesson has a question). */
  startReadiness(courseId: string): Observable<CourseStartReadiness> {
    return this.http
      .get<ApiEnvelope<CourseStartReadiness>>(`${this.base}/courses/${courseId}/start-readiness`)
      .pipe(map((r) => r.data));
  }

  enrollments(pageNumber = 1, pageSize = 50): Observable<PageResult<StudentCurriculumEnrollment>> {
    return this.http
      .get<ApiEnvelope<PageResult<StudentCurriculumEnrollment>>>(`${this.base}/enrollments`, {
        params: new HttpParams().set('pageNumber', pageNumber).set('pageSize', pageSize),
      })
      .pipe(map((r) => r.data));
  }

  /** Completed enrollments for the Finished courses section. */
  completedEnrollments(
    pageNumber = 1,
    pageSize = 50,
  ): Observable<PageResult<StudentCurriculumEnrollment>> {
    return this.http
      .get<ApiEnvelope<PageResult<StudentCurriculumEnrollment>>>(
        `${this.base}/enrollments/completed`,
        {
          params: new HttpParams().set('pageNumber', pageNumber).set('pageSize', pageSize),
        },
      )
      .pipe(map((r) => r.data));
  }

  catalog(pageNumber = 1, pageSize = 50): Observable<PageResult<StudentCourseCatalogItem>> {
    return this.http
      .get<ApiEnvelope<PageResult<StudentCourseCatalogItem>>>(this.catalogUrl, {
        params: new HttpParams().set('pageNumber', pageNumber).set('pageSize', pageSize),
      })
      .pipe(map((r) => r.data));
  }

  enroll(courseId: string): Observable<StudentCurriculumEnrollment> {
    return this.http
      .post<ApiEnvelope<StudentCurriculumEnrollment>>(`${this.catalogUrl}/${courseId}/enroll`, {})
      .pipe(map((r) => r.data));
  }

  updateEnrollmentStatus(
    enrollmentId: string,
    enrollmentStatus: 'ACTIVE' | 'INACTIVE',
  ): Observable<StudentCurriculumEnrollment> {
    return this.http
      .patch<ApiEnvelope<StudentCurriculumEnrollment>>(
        `${this.base}/enrollments/${enrollmentId}/status`,
        { enrollmentStatus },
      )
      .pipe(map((r) => r.data));
  }

  removeEnrollment(enrollmentId: string): Observable<void> {
    return this.http
      .delete<void>(`${this.base}/enrollments/${enrollmentId}`)
      .pipe(map(() => void 0));
  }

  saveLessonProgress(lessonId: string, accuracy: number): Observable<CurriculumLessonProgress> {
    return this.http
      .patch<ApiEnvelope<CurriculumLessonProgress>>(`${this.base}/lessons/${lessonId}/progress`, {
        accuracy,
      })
      .pipe(map((r) => r.data));
  }
}
